import { h } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import Panel from 'utils/panel';
import Icon from 'utils/icon';
import { formatMoney } from 'utils/storefront/format-resource';
import Image from "utils/image";

const ProductEngraving = (props) => {
  const { engravingProduct } = props;
  const { embossingProduct } = props;

  const [triggerEl, setTriggerEl] = useState(null);
  const closeTriggerEl = useRef(null);
  const inputRef = useRef(null);
  const [panelTriggers, setPanelTriggers] = useState({
    openTriggerEl: triggerEl,
    closeTriggerEl: null,
  });

  const [isDirty, setIsDirty] = useState(false);
  const [textOnImage, setTextOnImage] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [isValidEngraving, setIsValidEngraving] = useState(false);
  const [isFilledEngraving, setIsFilledEngraving] = useState(false);
  const [personalizedItemsChecked, setPersonalizedItemsChecked] = useState(
    false
  );
  const [isEmbossing, setIsEmbossing] = useState(false);
  const [engravingMsg,setEngravingMsg] = useState('')
  const [isFocusable,setIsFocusable] = useState(false);
  const designApplyRef = useRef(null);
  const personalizedInput = useRef(null)
  const togglePersonalizedItems = (e) => {
    if (e.type === 'click' || 
        (e.type === 'keydown' && 
        (e.key === ' ' || 
        e.key === 'Enter'))) {
      setPersonalizedItemsChecked(!personalizedItemsChecked);
    }
  }

  useEffect(() => {
    const variants = props?.product?.variants ?? [];
    const singleVariantEmbossing = variants.find(variant => variant.embossing);
    const variantEmbossing = singleVariantEmbossing?.embossing;
    if (embossingProduct && variantEmbossing) {
      setIsEmbossing(true);
    }
  }, [embossingProduct]);

  const validate = (e) => {
    const isEmptyEngraving  = checkEmptyEngraving(inputRef.current.value);
    const valid = checkValidEngraving(inputRef.current.value)
      && isEmptyEngraving
      && personalizedItemsChecked;

    if(!isEmptyEngraving) {
      inputRef.current.focus()
      setEngravingMsg(window.TMW.Strings.product.engraving.emptyError)
    } 

    if (isEmptyEngraving && 
      !personalizedItemsChecked 
      && e?.key === 'Tab' 
      || isEmptyEngraving 
      && !personalizedItemsChecked 
      && e?.key === 'Enter') {
      personalizedInput.current.focus()
      setEngravingMsg(window.TMW.Strings.product.engraving.personalizedItems)
    }

    return valid;
  }

  const checkValidEngraving = (value) => {
    const pattern = /^[A-Z]*$/;

    if (pattern.test(value.toUpperCase()) && value.length <= 3) {
      setTextOnImage(value.toUpperCase());
      setIsValidEngraving(true);
      return true;
    }

    setIsValidEngraving(false);
    return false;
  }

  const checkEmptyEngraving = (value) => {
    if (value.length) {
      setIsFilledEngraving(true);
      return true;
    }
    setIsFilledEngraving(false);
    return false;
  }

  const handleApplyDesign = (e) => {
    setIsDirty(true);

    if (!validate(e)) {
      return null;
    }

    const engravingApplyedEvent = new CustomEvent('engraving-applyed', {
      detail: {
        inputValue,
        engravingEventPayload: {
          merchandiseId: isEmbossing
            ? embossingProduct.id
            : engravingProduct.id,
          quantity: 1,
          attributes: isEmbossing
          ? [{ key: "_EmbossingProduct", value: true }]
          : [{ key: "_EngravingProduct", value: true }]
        }
      }
    });
    document.dispatchEvent(engravingApplyedEvent);
    setEngravingMsg(window.TMW.Strings.product.engraving.successMsg)
    const closePanel = new CustomEvent('engraving:close');
    document.dispatchEvent(closePanel);
    setTimeout(() => {
      document.querySelector('[data-edit-engraving]').focus();
    }, 500);
    return null;
  }

  const handleReset = () => {
    setTextOnImage('');
    setInputValue('');
    setPersonalizedItemsChecked(false);
    setIsFilledEngraving(false);
    setIsValidEngraving(false);
  }

  const handleEngravingOpened = (e) => {
    const { engravingRefElement } = e.detail;
    setIsDirty(false);

    if (engravingRefElement?.current) {
      setPanelTriggers({
        openTriggerEl: engravingRefElement.current,
        closeTriggerEl: closeTriggerEl.current ?
          [closeTriggerEl.current] : [],
      });
      setTriggerEl(engravingRefElement.current);
    }
    const openPanel = new CustomEvent('engraving:open');
    setEngravingMsg('')
    document.dispatchEvent(openPanel);
  };

  const hadnleEngravingRemoved = () => {
    setEngravingMsg('')
    setInputValue('');
    setTextOnImage('');
    setPersonalizedItemsChecked(false)
  }

  useEffect(() => {
    document.addEventListener('engraving-opened', handleEngravingOpened);
    document.addEventListener('engraving-removed', hadnleEngravingRemoved);

    return () => {
      document.removeEventListener('engraving-opened', handleEngravingOpened);
      document.removeEventListener('engraving-removed', hadnleEngravingRemoved);
    };
  }, []);

  useEffect(() => {
    setPanelTriggers((prevTriggers) => ({
      ...prevTriggers,
      openTriggerEl: triggerEl,
    }));
  }, [triggerEl]);

  useEffect(() => {
    if (!closeTriggerEl.current) return;
    setPanelTriggers((value) => ({
      ...value,
      closeTriggerEl: [closeTriggerEl.current],
    }));
  }, [closeTriggerEl, closeTriggerEl.current]);

  const handleInputChange = (e) => {
    const value = inputRef.current.value.toUpperCase();

    if (value.length <= 3) {
      setInputValue(value);
    }

    validate(e);
  };


  const focusHandle = (focusedItem, e) => {
    if (!e.shiftKey && e.key === 'Tab') {
      e.preventDefault();
      focusedItem.focus();
    } 
  };
  const backFocusHandle = (focusedItem, e) => {
    if (e.shiftKey && e.key === 'Tab') {
      e.preventDefault();
      focusedItem.focus();
    } 
  }

  return (
    <Panel overlay={true} triggers={panelTriggers} defaultOpen={false} context="engraving" additionalClasses='lg:w-[420px]' childrenVisible={setIsFocusable}>
      <div className='h-[100dvh]'>
        <div className='overflow-y-auto h-[calc(100dvh-157px)]'>
          <div className='flex justify-between py-[14px] px-5 border-b border-grey-3 items-center'>
            <p className='utility-1 uppercase'>
              {isEmbossing
                ? window.TMW.Strings.product.embossing.title
                : window.TMW.Strings.product.engraving.title}
            </p>
            <button type="button" ref={closeTriggerEl} tabIndex="-1" 
              className='cursor-pointer' 
              aria-label="Close engraving dialog"
              onKeyDown={(e)=>backFocusHandle(designApplyRef.current,e)}
                >
              <Icon name='close' />
            </button>
          </div>
          <div className={`${!isFocusable ? 'hidden' : ''}`}>
          <div className={`flex flex-col gap-5 relative ${!isFocusable ? 'hidden' : ''}`}>
            <Image
              classes="h-auto w-[420px] aspect-square"
              src={isEmbossing ? embossingProduct.embossingImage : 
              engravingProduct.engravingImage}
              srcWidths={{sm: 200, md: 420, lg: 420, xl: 420, '2xl': 420}}
            />
            <div className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'>
              <p className='font-serif text-[80px] mt-10 text-engraving' data-text-on-image>
                {textOnImage}
              </p>
            </div>
          </div>
          <div className='flex flex-col py-5 px-5 gap-1.5'>
            <p className='utility-3 uppercase'>
              {isEmbossing
                ? window.TMW.Strings.product.embossing.yourEmbossing
                : window.TMW.Strings.product.engraving.yourEngraving}
            </p>
            <div className='flex items-center relative'>
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onInput={handleInputChange}
                className={`${isDirty && (!isValidEngraving || !isFilledEngraving) ? 'border-error' : 'border-grey-3'} border  p-2 pt-[17px] pb-[15px] w-full paragraph-5`}
                maxLength="3"
                placeholder="Type here"
              />
              <span className='absolute right-2.5 top-1/2 -translate-y-1/2'>{`${inputValue.length}/3`}</span>
            </div>
            {
              (isDirty && (!isFilledEngraving)) && (
                <p className='paragraph-6 text-error'>
                  {window.TMW.Strings.product.engraving.emptyError}
                </p>
              )
            }
            <p className={`${isDirty && !isValidEngraving ? 'text-error' : 'text-grey-9'} paragraph-6 pb-[14px]`}>{window.TMW.Strings.product.engraving.acceptLetters}</p>
            <p className='paragraph-6 text-grey-9'>
              {window.TMW.Strings.product.engraving.shipping}
            </p>
          </div>
          </div>
        </div>
        <div className={`flex flex-col fixed bottom-0 max-w-full py-5 px-5 border-t border-grey-3 gap-8 bg-white z-[50] lg:px-8 ${!isFocusable ? 'hidden' : ''}`}>
          <div className=
            {`input-wrapper flex items-center gap-3 relative relative cursor-pointer ${isDirty && !personalizedItemsChecked && 'error'}`}
            onClick={togglePersonalizedItems}
            tabIndex="0"
            onKeyDown={togglePersonalizedItems}
            role="checkbox"
            ref={personalizedInput}
            aria-checked={personalizedItemsChecked}
            aria-label={`${window.TMW.Strings.product.
              engraving.personalizedItems}`}
            aria-invalid={`${isDirty && 
              !personalizedItemsChecked && true}`}
            aria-required={`${isDirty && 
              !personalizedItemsChecked && true}`}
          >
           
            <span className={`absolute -top-8 z-1 left-4 paragraph-6 
            bg-white p-2 border border-grey-3 hidden 
              ${isDirty && !personalizedItemsChecked && '!inline'}`}>
              {window.TMW.Strings.product.engraving.personalizedItemsRequired}
            </span>
            <input
              className='input-checkbox--outline'
              type='checkbox'
              name='personalized_items'
              id='personalized_items'
              checked={personalizedItemsChecked}
              onChange={() => { }}
              required
            />
            <label
              htmlFor='personalized_items'
              className={`
                paragraph-5 pt-0.5 !pl-6 lg:paragraph-4 
                ${isDirty && !personalizedItemsChecked && 'personalized-items'}`
              }
              
              
            >
              <span className={`
                paragraph-5 lg:paragraph-4
                ${isDirty && !personalizedItemsChecked && 'text-error'}`
              }
              >
                {window.TMW.Strings.product.engraving.personalizedItems}
              </span>
            </label>
            <Icon name="icon-check" customClasses='absolute w-2.5' />
          </div>
          <div className='flex flex-col gap-2.5'>
            <div className='flex justify-between'>
              <p className='utility-1 uppercase'>
                {window.TMW.Strings.product.engraving.subtotal}
              </p>
              <p className='utility-1'>
                {formatMoney({
                  amount: isEmbossing
                    ? embossingProduct.amount
                    : engravingProduct.amount,
                  currencyCode: window.TMW?.localization?.currencyCode || 'USD',
                })}
              </p>
            </div>
            <div className='flex gap-4'>
              <button
                className="button button--dark-outline min-w-[160px] lg:min-w-[170px]"
                onClick={handleReset}
              >
                {window.TMW.Strings.product.engraving.reset}
              </button>
              <button
                className="button button--dark min-w-[160px] px-2.5 lg:min-w-[170px]"
                data-apply-design
                onClick={handleApplyDesign}
                ref={designApplyRef}
                onKeyDown={(e) => {
                  focusHandle(closeTriggerEl.current, e);
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleApplyDesign(e);
                  }
                }}
              >
                {window.TMW.Strings.product.engraving.applyDesign}
              </button>
              <div className='sr-only' 
                aria-live="assertive" 
                aria-atomic="true" 
                role="status">{engravingMsg}</div>
            </div>
          </div>
        </div>
      </div>
    </Panel>
  );
};

export default ProductEngraving;

